import classNames from 'classnames';

import EDHighlightedText from '../Autocomplete/Components/EDHighlightedText';
import { getTextParts } from '../Autocomplete/Utils/AutocompleteUtils';
import EDCheckbox from '../Checkboxes/EDCheckbox';
import EDIcon from '../Icon/EDIcon';
import { EDListItemButton } from '../List/Components/EDListItem';
import EDListItemText from '../List/Components/EDListItemText';
import EDTypography from '../Typography/EDTypography';
import { makeStyles } from 'tss-react/mui';
import { ITreeSection } from './TreeSelectUtils';
import { sublistNotAvailableItemValue } from '../../Components/Urval/Components/UrvalFieldPicker/Utils/UrvalFieldsUtils';

interface IProps<T> {
  parentSection?: ITreeSection;
  section: ITreeSection;
  showPointerLines?: number;
  level: number;
  checked?: boolean;
  indeterminate?: boolean;
  checkBoxDisabled?: boolean;
  checkBoxDisabledReason?: string;
  checkBoxTooltip?: string;
  checkboxNoClickPropagation?: boolean;
  fontBold?: boolean;
  searchValue?: string;
  onListItemClick?: (event: React.MouseEvent<any>) => void;
  onCheck?: (isInputChecked: boolean, event: any) => void;
  renderSecondaryText?: (suggestion: ITreeSection<T>) => React.ReactNode;
  showListItemButtons?: (section: ITreeSection<T>) => boolean;
  hoverChecklistButtons?: (analys: ITreeSection<T>) => boolean;
  renderListItemButtons?: (
    section: ITreeSection<T>,
    parent?: ITreeSection<T>
  ) => React.ReactNode;
  classes?: any;
}

const useStyles = makeStyles<IProps<unknown>, 'listItem'>({
  uniqId: '31d75254-cf2f-4634-84e6-f6c6ce562206',
})(
  (theme, _params, classes) =>
    ({
      listItem: {},
      hoverButtons: {
        opacity: 0,
        transition: 'opacity 100ms ease',
        [`.${classes.listItem}:hover &`]: {
          opacity: 1,
        },
      },
      itemContainer: {
        paddingTop: 0,
        paddingBottom: 0,
        whiteSpace: 'normal',
        display: 'block',
      },
      fontSize: {
        fontSize: '0.875rem',
        fontWeight: '400',
      },
      analysItemContainer: {
        position: 'relative',
        display: 'flex',
        flex: 1,
        alignItems: 'center',
        justifyContent: 'space-between',
      },
      newFieldButton: {
        marginRight: theme.spacing(3),
      },
      listItemEndContainer: {
        display: 'flex',
        alignItems: 'center',
      },
      horizontalNodeLine: {
        '&:before': {
          position: 'absolute',
          left: `calc(-${theme.spacing(2)})`,
          width: theme.spacing(2),
          content: '""',
          borderBottom: '2px solid #d7d7d7',
        },
      },
      emptyListItem: {
        height: theme.spacing(5.25),
      },
    } as const)
);

const EDTreeSelectListItemBase = <T,>(props: IProps<T>) => {
  const { classes, cx } = useStyles(props as IProps<unknown>);
  const isLeafNode =
    !props.section.subList || props.section.subList.length === 0;
  const isClickable = isLeafNode && !props.section.isExpandable;

  const handleRowClick = (e: React.MouseEvent) => {
    if (!isClickable || props.checkBoxDisabled) return;
    // Dont trigger if clicking directly on the checkbox
    if ((e.target as HTMLElement).closest('input[type="checkbox"]')) return;

    props.onCheck?.(!props.checked, e);
  };

  return (
    <EDListItemButton
      disableTabbing
      className={cx('arrowMoveTarget', classes.listItem)}
      onClick={props.onListItemClick}
      dense
      disableGutters
      testId={`tree-select-listitem-${props.section.name}`}
    >
      <div
        className={classNames(classes.analysItemContainer, {
          [classes.horizontalNodeLine]:
            props.showPointerLines && props.level >= props.showPointerLines,
        })}
        onClick={handleRowClick}
      >
        {props.section.value !== sublistNotAvailableItemValue && (
          <>
            <EDCheckbox
              onCheck={props.onCheck}
              label=""
              checked={props.checked}
              indeterminate={props.indeterminate}
              disabled={props.checkBoxDisabled}
              disabledReason={props.checkBoxDisabledReason}
              tooltip={props.checkBoxTooltip}
              flexShrink={0}
              noClickPropagation={props.checkboxNoClickPropagation}
              testId={`tree-select-checkbox-${props.section.name}`}
            />
            <EDListItemText
              disableTypography
              noLeftPadding
              primary={
                <EDTypography
                  className={classNames(classes.fontSize)}
                  noWrap={false}
                >
                  {!props.section.selected && (
                    <EDHighlightedText
                      parts={getTextParts(
                        props.section.name,
                        props.searchValue || ''
                      )}
                      wordBreak
                      fontBold={props.fontBold}
                    />
                  )}
                  {props.section.selected && props.section.name}
                  {props.section.disabled?.labelSuffix && (
                    <span>{props.section.disabled.labelSuffix}</span>
                  )}
                </EDTypography>
              }
              secondary={props.renderSecondaryText?.(props.section)}
            />
            <div className={classes.listItemEndContainer}>
              {(!props.showListItemButtons ||
                props.showListItemButtons?.(props.section)) && (
                <div
                  className={classNames({
                    [classes.hoverButtons]: props.hoverChecklistButtons?.(
                      props.section
                    ),
                  })}
                >
                  {props.renderListItemButtons?.(
                    props.section,
                    props.parentSection
                  )}
                </div>
              )}
              {(props.section.isExpandable ||
                (props.section.subList &&
                  props.section.subList.length > 0)) && (
                <EDIcon
                  marginRight={1}
                  paddingRight={2}
                  iconType={
                    props.section.open ? 'arrow_drop_up' : 'arrow_drop_down'
                  }
                />
              )}
            </div>
          </>
        )}
        {props.section.value === sublistNotAvailableItemValue && (
          <div className={classes.emptyListItem} />
        )}
      </div>
    </EDListItemButton>
  );
};

export default EDTreeSelectListItemBase;
